import { motion } from "framer-motion";
import { HOW } from "@/constants/testIds";

const steps = [
  {
    n: "01",
    title: "Contact us for a quote",
    body: "Reach out by phone or email. Tell us what you need delivered, from where, and when. We reply quickly with a straightforward quote.",
    img: "/how-quote.jpg",
    imgAlt: "Line illustration of a phone call and a handwritten quote note",
  },
  {
    n: "02",
    title: "Get your quote, we'll get your driver",
    body: "Approve the quote and we assign a background-checked driver from our roster. You'll get their name, vehicle, and ETA.",
    img: "/how-driver.jpg",
    imgAlt: "Line illustration of a courier driver beside a delivery van with a route pin",
  },
  {
    n: "03",
    title: "Delivery with proof",
    body: "Your driver completes the delivery and sends proof — photo, signature, or receipt — via SMS or email. Nothing left to wonder.",
    img: "/how-proof.jpg",
    imgAlt: "Line illustration of a smartphone photographing a delivered parcel with a checkmark",
  },
];

function Row({ step, i }) {
  return (
    <motion.div
      data-testid={HOW.step(i + 1)}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="grid grid-cols-12 gap-6 md:gap-10 items-start py-10 md:py-14 border-t border-forest/15"
    >
      <div className="col-span-3 md:col-span-2">
        <div className="chapter-num text-[64px] md:text-[100px] text-terracotta">
          {step.n}
        </div>
      </div>
      <div className="col-span-9 md:col-span-6">
        <h3 className="font-serif text-2xl md:text-4xl text-forest tracking-tight leading-[1.1]">
          {step.title}
        </h3>
        <p className="mt-4 text-base md:text-lg text-charcoal/80 max-w-xl leading-relaxed">
          {step.body}
        </p>
      </div>
      <div className="col-span-12 md:col-span-4 md:pl-8 flex md:justify-end items-start">
        <img
          src={step.img}
          alt={step.imgAlt}
          data-testid={`how-illustration-${step.n}`}
          className="w-44 h-44 md:w-64 md:h-64"
          loading="lazy"
        />
      </div>
    </motion.div>
  );
}

export default function HowItWorks() {
  return (
    <section
      id="how"
      data-testid={HOW.root}
      className="bg-paper py-24 md:py-32"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 md:gap-10 mb-8 md:mb-14">
          <div className="col-span-12 md:col-span-5">
            <div className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">
              How we work
            </div>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] text-forest tracking-tight">
              Three steps. No <span className="italic font-light">surprises</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-base md:text-lg text-charcoal/70 max-w-lg">
              Quoting a courier shouldn't feel like a project. We keep it small,
              human, and clear — from the first call to the signed receipt.
            </p>
          </div>
        </div>

        <div>
          {steps.map((s, i) => (
            <Row key={s.n} step={s} i={i} />
          ))}
          <div className="rule mt-2" />
        </div>
      </div>
    </section>
  );
}
